/* Required per-line availability preferences for managed Bulk pounds. */
(() => {
  if (window.__pjBulkSubstitutionLoaded) return;
  window.__pjBulkSubstitutionLoaded = true;

  const VERSION = 'bulk-substitution-2026-10-20-v8';
  const PACKING_NOTE_START = '[BULK AVAILABILITY PLAN]';
  const PACKING_NOTE_END = '[/BULK AVAILABILITY PLAN]';
  const CATALOG_SECTION_URL = 'collections/bulk-thca-flower?section_id=pj-bulk-substitution-catalog';
  const COLLECTION_URL = 'collections/bulk-thca-flower/products.json?limit=250';
  const root = window.Shopify?.routes?.root || '/';
  const managedKeys = new Set([
    '_pj_bulk_resolution',
    '_pj_bulk_resolution_version',
    '_pj_bulk_replacement_handle',
    '_pj_bulk_replacement_variant_id',
    '_pj_bulk_replacement_title',
    '_pj_bulk_replacement_fallback',
    'Availability preference',
    'Backup strain',
    'If backup is unavailable',
    'Price difference',
    'Refund timing'
  ]);
  let catalogPromise;
  let updateQueue = Promise.resolve();
  let noteSyncQueue = Promise.resolve();

  async function request(path, data) {
    const response = await fetch(root + path, {
      method: data ? 'POST' : 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {'Accept': 'application/json', ...(data ? {'Content-Type': 'application/json'} : {})},
      ...(data ? {body: JSON.stringify(data)} : {})
    });
    const body = await response.json();
    if (!response.ok) throw new Error(body.description || 'Your cart could not be updated. Please try again.');
    return body;
  }

  function sectionIds() {
    return [...new Set(['cart-button', ...[...document.querySelectorAll('cart-section,cart-drawer')]
      .map(element => element.closest('.shopify-section')?.id.replace(/^shopify-section-/, ''))
      .filter(Boolean)])].slice(0, 5);
  }

  function lines() {
    return [...document.querySelectorAll('[data-bulk-resolution-line]')];
  }

  function representativeLines() {
    const byKey = new Map();
    for (const line of lines()) if (!byKey.has(line.dataset.lineKey)) byKey.set(line.dataset.lineKey, line);
    return [...byKey.values()];
  }

  function withoutManagedPackingNote(note = '') {
    const start = note.indexOf(PACKING_NOTE_START);
    if (start < 0) return note.trim();
    const end = note.indexOf(PACKING_NOTE_END, start);
    if (end < 0) return note.slice(0, start).trim();
    return `${note.slice(0, start)}${note.slice(end + PACKING_NOTE_END.length)}`.trim();
  }

  function packingNoteForCart(cart) {
    const plans = (cart.items || []).flatMap(item => {
      const properties = item.properties || {};
      const preference = properties['Availability preference'];
      if (!preference) return [];
      const lines = [
        item.product_title || item.title,
        `Preference: ${preference}`
      ];
      if (properties['Backup strain']) lines.push(`Backup strain: ${properties['Backup strain']}`);
      if (properties['If backup is unavailable']) lines.push(`If backup is unavailable: ${properties['If backup is unavailable']}`);
      if (properties['Price difference']) lines.push(`Price handling: ${properties['Price difference']}`);
      if (properties['Refund timing']) lines.push(`Refund timing: ${properties['Refund timing']}`);
      return [lines.join('\n')];
    });
    if (!plans.length) return '';
    return [
      PACKING_NOTE_START,
      ...plans,
      'FULFILLMENT: Contact Laszlo before any refund, store credit, invoice, or higher-priced substitution. Do not pack a higher-priced replacement until payment is confirmed.',
      PACKING_NOTE_END
    ].join('\n\n');
  }

  async function syncPackingNote(cart) {
    const current = cart || await request('cart.js');
    const customerNote = withoutManagedPackingNote(current.note || '');
    const packingNote = packingNoteForCart(current);
    const nextNote = [customerNote, packingNote].filter(Boolean).join('\n\n');
    if (nextNote === (current.note || '')) return current;
    return request('cart/update.js', {note: nextNote});
  }

  function queuePackingNoteSync(cart) {
    noteSyncQueue = noteSyncQueue.catch(() => {}).then(() => syncPackingNote(cart));
  }

  function dispatchState() {
    const uniqueLines = representativeLines();
    const ready = uniqueLines.length > 0 && uniqueLines.every(line => {
      const choice = line.dataset.selectedChoice || '';
      if (choice === 'refund_original' || choice === 'store_credit') return true;
      if (choice !== 'substitute') return false;
      const replacement = line.dataset.selectedReplacementHandle || '';
      const fallback = line.dataset.selectedFallback || '';
      const option = [...line.querySelector('[data-bulk-replacement]')?.options || []]
        .find(entry => entry.value === replacement && entry.dataset.available === 'true');
      return Boolean(option && (fallback === 'store_credit' || fallback === 'refund_original'));
    });
    document.dispatchEvent(new CustomEvent('pj:bulk-resolution-state', {detail: {ready, lineCount: uniqueLines.length}}));
    for (const panel of document.querySelectorAll('pj-bulk-cart')) {
      const summary = panel.querySelector('[data-bulk-resolution-summary]');
      if (summary) summary.textContent = ready
        ? 'Backup plans saved for every Bulk item.'
        : 'Choose and save a backup plan under every Bulk item.';
    }
  }

  function setExplanation(line) {
    const choice = line.querySelector('[data-bulk-resolution-choice]')?.value || '';
    const detail = line.querySelector('[data-bulk-replacement-fields]');
    const explanation = line.querySelector('[data-bulk-resolution-explanation]');
    if (detail) detail.hidden = choice !== 'substitute';
    if (!explanation) return;
    if (choice === 'refund_original') {
      explanation.textContent = 'We will submit the refund to the payment method used at checkout. Credit-card refunds may take up to 3 business days to appear after processing.';
    } else if (choice === 'store_credit') {
      explanation.textContent = 'Store credit is available immediately after we issue it to your Plain Jane customer account, so you can reorder without waiting on your bank.';
    } else if (choice === 'substitute') {
      explanation.textContent = 'Choose a currently available backup strain. If it costs less, we will refund the difference. If it costs more, we will email a follow-up invoice and only switch after it is paid.';
    } else {
      explanation.textContent = '';
    }
  }

  function setBusy(line, busy, message = '') {
    line.toggleAttribute('data-saving', busy);
    for (const input of line.querySelectorAll('select')) input.disabled = busy;
    const status = line.querySelector('[data-bulk-resolution-line-status]');
    if (status) status.textContent = message;
  }

  async function catalogPayload() {
    try {
      const response = await fetch(root + CATALOG_SECTION_URL, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {'Accept': 'text/html'}
      });
      if (!response.ok) throw new Error('Catalog details could not be loaded.');
      const documentNode = new DOMParser().parseFromString(await response.text(), 'text/html');
      const catalogNode = documentNode.querySelector('[data-pj-bulk-substitution-catalog]');
      if (!catalogNode?.textContent) throw new Error('Catalog details were missing.');
      return JSON.parse(catalogNode.textContent);
    } catch (error) {
      return request(COLLECTION_URL);
    }
  }

  function loadCatalog() {
    if (!catalogPromise) {
      catalogPromise = catalogPayload().then(payload => (payload.products || [])
        .filter(product => (product.productType || product.product_type) === 'Bulk THCA Flower' && product.available !== false)
        .map(product => {
          const variant = product.variantId ? {
            id: product.variantId,
            price: product.price,
            available: product.available
          } : (product.variants || []).find(entry => entry.available);
          const familyTag = (product.tags || []).find(tag => tag.startsWith('Strain Family: '));
          return variant ? {
            handle: product.handle,
            title: product.title,
            variantId: String(variant.id),
            family: product.family || (familyTag ? familyTag.replace('Strain Family: ', '') : ''),
            price: Number(variant.price),
            thca: product.thca === null || product.thca === undefined || product.thca === ''
              ? null
              : Number(product.thca)
          } : null;
        })
        .filter(Boolean)
        .sort((left, right) => left.title.localeCompare(right.title)));
    }
    return catalogPromise;
  }

  function familyDistance(currentFamily, candidateFamily) {
    if (!currentFamily || !candidateFamily) return 3;
    if (currentFamily === candidateFamily) return 0;
    if (currentFamily === 'Hybrid' || candidateFamily === 'Hybrid') return 1;
    return 2;
  }

  function priceDifference(currentPrice, candidatePrice) {
    if (!Number.isFinite(currentPrice) || !Number.isFinite(candidatePrice)) return Number.POSITIVE_INFINITY;
    return Math.abs(candidatePrice - currentPrice);
  }

  function shortTitle(title) {
    return title.replace(/\s+[–—-]\s+Bulk THCA Flower 1 lb$/i, '').trim();
  }

  function optionLabel(item, currentPrice) {
    const potency = Number.isFinite(item.thca)
      ? `${new Intl.NumberFormat('en-US', {maximumFractionDigits: 1}).format(item.thca)}% THCA`
      : 'THCA not listed';
    const details = [];
    if (Number.isFinite(item.price) && Number.isFinite(currentPrice)) {
      const difference = item.price - currentPrice;
      if (Math.abs(difference) < 0.005) details.push('Same price');
      else details.push(`${new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(Math.abs(difference))} ${difference < 0 ? 'less' : 'more'}`);
    }
    if (item.family) details.push(item.family);
    details.push(potency);
    return `${shortTitle(item.title)} · ${details.join(' · ')}`;
  }

  function appendOption(select, item, selectedHandle, currentPrice) {
    const option = document.createElement('option');
    option.value = item.handle;
    option.textContent = optionLabel(item, currentPrice);
    option.dataset.variantId = item.variantId;
    option.dataset.title = item.title;
    option.dataset.family = item.family;
    option.dataset.price = String(item.price);
    option.dataset.available = 'true';
    option.selected = item.handle === selectedHandle;
    select.append(option);
  }

  function updateReplacementLink(line) {
    const select = line.querySelector('[data-bulk-replacement]');
    const link = line.querySelector('[data-bulk-replacement-link]');
    const option = select?.selectedOptions?.[0];
    if (!link) return;
    if (!option?.value || option.dataset.available !== 'true') {
      link.hidden = true;
      link.removeAttribute('href');
      link.removeAttribute('aria-label');
      return;
    }
    link.href = `${root}products/${encodeURIComponent(option.value)}`;
    link.textContent = 'View strain details ↗';
    link.setAttribute('aria-label', `View ${shortTitle(option.dataset.title || 'selected strain')} in a new tab`);
    link.hidden = false;
  }

  async function hydrateLine(line, catalog) {
    const select = line.querySelector('[data-bulk-replacement]');
    if (!select || select.dataset.hydrated === 'true') return;
    const selectedHandle = line.dataset.selectedReplacementHandle || '';
    const currentItem = catalog.find(item => item.handle === line.dataset.currentHandle);
    const currentFamily = currentItem?.family || line.dataset.currentFamily || '';
    const currentPrice = currentItem?.price ?? Number(line.dataset.currentPrice);
    const candidates = catalog
      .filter(item => item.handle !== line.dataset.currentHandle)
      .map(item => ({
        ...item,
        familyDistance: familyDistance(currentFamily, item.family),
        priceDifference: priceDifference(currentPrice, item.price)
      }))
      .map(item => ({
        ...item,
        samePrice: item.priceDifference < 0.005
      }))
      .sort((left, right) => Number(right.samePrice) - Number(left.samePrice) ||
        left.priceDifference - right.priceDifference ||
        left.familyDistance - right.familyDistance || left.title.localeCompare(right.title));
    select.innerHTML = '<option value="">Choose a backup strain</option>';
    for (const item of candidates) appendOption(select, item, selectedHandle, currentPrice);
    if (selectedHandle && ![...select.options].some(option => option.value === selectedHandle)) {
      const unavailable = document.createElement('option');
      unavailable.value = selectedHandle;
      unavailable.textContent = `${line.dataset.selectedReplacementTitle || 'Previously selected backup'} — no longer available`;
      unavailable.selected = true;
      unavailable.dataset.available = 'false';
      select.append(unavailable);
      const status = line.querySelector('[data-bulk-resolution-line-status]');
      if (status) status.textContent = 'Your saved backup is no longer available. Please choose another option.';
    }
    select.dataset.hydrated = 'true';
    setExplanation(line);
    updateReplacementLink(line);
  }

  async function hydrate() {
    const currentLines = lines();
    if (!currentLines.length) {
      document.dispatchEvent(new CustomEvent('pj:bulk-resolution-state', {detail: {ready: true, lineCount: 0}}));
      return;
    }
    try {
      const catalog = await loadCatalog();
      await Promise.all(currentLines.map(line => hydrateLine(line, catalog)));
    } catch (error) {
      for (const line of currentLines) {
        const select = line.querySelector('[data-bulk-replacement]');
        if (select) select.innerHTML = '<option value="">Replacement strains could not be loaded</option>';
        const status = line.querySelector('[data-bulk-resolution-line-status]');
        if (status) status.textContent = 'Replacement strains could not be loaded. You can still choose either refund option.';
      }
    }
    dispatchState();
  }

  function preferenceFromLine(line) {
    const choice = line.querySelector('[data-bulk-resolution-choice]')?.value || '';
    if (choice === 'refund_original') return {
      choice,
      publicPreference: 'Refund to original payment method',
      refundTiming: 'Credit-card refunds may take up to 3 business days',
      replacement: null,
      fallback: ''
    };
    if (choice === 'store_credit') return {
      choice,
      publicPreference: 'Refund to Plain Jane store credit',
      refundTiming: 'Available immediately once issued',
      replacement: null,
      fallback: ''
    };
    if (choice !== 'substitute') return null;
    const select = line.querySelector('[data-bulk-replacement]');
    const option = select?.selectedOptions?.[0];
    const fallback = line.querySelector('[data-bulk-replacement-fallback]')?.value || '';
    if (!option?.value || option.dataset.available !== 'true' || !['store_credit', 'refund_original'].includes(fallback)) return null;
    return {
      choice,
      publicPreference: 'Use my backup strain',
      refundTiming: '',
      replacement: {handle: option.value, variantId: option.dataset.variantId, title: option.dataset.title},
      fallback
    };
  }

  async function savePreference(line) {
    const preference = preferenceFromLine(line);
    if (!preference) {
      dispatchState();
      return;
    }
    setBusy(line, true, 'Saving your preference…');
    try {
      const cart = await request('cart.js');
      const item = cart.items.find(entry => entry.key === line.dataset.lineKey);
      if (!item) throw new Error('This cart item changed. Please review the refreshed cart and try again.');
      const properties = {...(item.properties || {})};
      for (const key of managedKeys) delete properties[key];
      properties._pj_bulk_resolution = preference.choice;
      properties._pj_bulk_resolution_version = VERSION;
      properties['Availability preference'] = preference.publicPreference;
      if (preference.refundTiming) properties['Refund timing'] = preference.refundTiming;
      if (preference.replacement) {
        properties._pj_bulk_replacement_handle = preference.replacement.handle;
        properties._pj_bulk_replacement_variant_id = preference.replacement.variantId;
        properties._pj_bulk_replacement_title = preference.replacement.title;
        properties._pj_bulk_replacement_fallback = preference.fallback;
        properties['Backup strain'] = preference.replacement.title;
        properties['Price difference'] = 'Refund the difference if lower; email an invoice before a higher-priced replacement';
        properties['If backup is unavailable'] = preference.fallback === 'store_credit'
          ? 'Refund to store credit — available immediately once issued'
          : 'Refund to original payment method — cards may take up to 3 business days';
      }
      const response = await request('cart/change.js', {
        id: item.key,
        quantity: item.quantity,
        properties,
        sections: sectionIds(),
        sections_url: location.pathname + location.search
      });
      await syncPackingNote(response);
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: {type: 'change', request: {}, response, pjBulkResolution: true}
      }));
    } catch (error) {
      setBusy(line, false, error.message || 'Your preference could not be saved. Please try again.');
      dispatchState();
    }
  }

  function queueSave(line) {
    updateQueue = updateQueue.catch(() => {}).then(() => savePreference(line));
  }

  document.addEventListener('change', event => {
    const line = event.target.closest?.('[data-bulk-resolution-line]');
    if (!line) return;
    setExplanation(line);
    updateReplacementLink(line);
    if (event.target.matches('[data-bulk-resolution-choice]') && event.target.value !== 'substitute') {
      queueSave(line);
    } else if (preferenceFromLine(line)) {
      queueSave(line);
    } else {
      dispatchState();
    }
  });

  document.addEventListener('cart:updated', event => {
    queueMicrotask(hydrate);
    if (!event.detail?.pjBulkResolution) queuePackingNoteSync();
  });
  const observer = new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 &&
      (node.matches?.('[data-bulk-resolution-line]') || node.querySelector?.('[data-bulk-resolution-line]'))))) {
      queueMicrotask(hydrate);
    }
  });
  observer.observe(document.body, {childList: true, subtree: true});
  hydrate();
})();
